export const QUERY_MIN_LENGTH = 2;
export const QUERY_MAX_LENGTH = 150;

const SESSION_PATH = '/api/v1/public/session';

export class PatientGptSessionError extends Error {
    constructor({ kind, status, code, message }) {
        super(message);
        this.name = 'PatientGptSessionError';
        this.kind = kind; // 'http' | 'network' | 'malformed'
        this.status = status; // number | null
        this.code = code; // string | null
    }
}

/**
 * Initiate a K Health PatientGPT session.
 *
 * @param {object}  args
 * @param {string}  args.apiBaseUrl       Origin + optional base path, from the Sling model (§5.2).
 *                                        Supplied by the caller: this module is framework-agnostic
 *                                        and has no access to AEM-delivered props.
 * @param {string}  args.query            The patient's health question. 2-150 chars (caller-validated).
 * @param {object}  [args.utm_parameters] Optional flat key-value UTM params (one key per present
 *                                        UTM field), supplied by the PatientGptBanner Feature Module
 *                                        from `libs/utm`'s `readUtmParams()`. Included in the request
 *                                        body only when provided and non-empty; omitted otherwise
 *                                        (AHSMKTG-4017, patient-gpt-dynamic-utm R-01).
 * @returns {Promise<{ url: string }>}  Resolves with the tokenized session URL from the 200 body.
 * @throws  {PatientGptSessionError}    Typed failure — see "Error mapping" below.
 */
export async function createSession({ apiBaseUrl, query, utm_parameters }) {
    let response;

    const filteredUtm = utm_parameters
        ? Object.fromEntries(Object.entries(utm_parameters).filter(([, v]) => Boolean(v)))
        : null;
    const requestBody =
        filteredUtm && Object.keys(filteredUtm).length > 0
            ? { query, utm_parameters: filteredUtm }
            : { query };

    try {
        response = await fetch(`${apiBaseUrl}${SESSION_PATH}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(requestBody),
        });
    } catch {
        throw new PatientGptSessionError({
            kind: 'network',
            status: null,
            code: null,
            message: 'PatientGPT session request failed: network error',
        });
    }

    if (!response.ok) {
        const message = `PatientGPT session request failed: ${response.status} ${response.statusText}`;
        let code = null;

        try {
            const errorBody = await response.json();
            if (typeof errorBody?.error?.code === 'string') {
                code = errorBody.error.code;
            }
        } catch {
            // Non-JSON body — code remains null; message stays status-line-synthesized
        }

        throw new PatientGptSessionError({
            kind: 'http',
            status: response.status,
            code,
            message,
        });
    }

    let body;
    try {
        body = await response.json();
    } catch {
        throw new PatientGptSessionError({
            kind: 'malformed',
            status: 200,
            code: null,
            message: 'PatientGPT session request failed: 200 response body is not parseable JSON',
        });
    }

    const trimmed = typeof body?.url === 'string' ? body.url.trim() : '';
    if (trimmed !== '') {
        let parsed;
        try {
            parsed = new URL(trimmed);
        } catch {
            parsed = null;
        }
        if (parsed?.protocol === 'https:') {
            return { url: trimmed };
        }
    }

    throw new PatientGptSessionError({
        kind: 'malformed',
        status: 200,
        code: null,
        message: 'PatientGPT session request failed: 200 response body contains no usable url',
    });
}
